import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
  read: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
  replied: 'bg-sky-500/20 text-sky-400 border-sky-500/50',
  archived: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
};

export default function ContactMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyFeedback, setReplyFeedback] = useState('');

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/contact/admin/messages`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load messages');
      const data = await res.json();
      setMessages(data.messages || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const openMessage = async (m) => {
    setSelected(m);
    setShowReply(false);
    setReplyText('');
    setReplyFeedback('');
    if (m.status === 'new') {
      try {
        await fetch(`${API_URL}/api/contact/admin/messages/${m.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ status: 'read' }),
        });
        setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, status: 'read' } : x)));
      } catch { /* ignore */ }
    }
  };

  const setStatus = async (m, status) => {
    await fetch(`${API_URL}/api/contact/admin/messages/${m.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify({ status }),
    });
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, status } : x)));
    if (selected?.id === m.id) setSelected({ ...m, status });
  };

  const del = async (m) => {
    if (!window.confirm(`Delete message from ${m.email}?`)) return;
    await fetch(`${API_URL}/api/contact/admin/messages/${m.id}`, { method: 'DELETE', credentials: 'include' });
    setMessages((prev) => prev.filter((x) => x.id !== m.id));
    if (selected?.id === m.id) setSelected(null);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    setReplyFeedback('');
    try {
      const res = await fetch(`${API_URL}/api/contact/admin/messages/${selected.id}/reply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ message: replyText.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send reply');
      const newReply = data.reply;
      setMessages((prev) => prev.map((x) => (x.id === selected.id ? { ...x, status: 'replied' } : x)));
      setSelected((prev) => ({ ...prev, status: 'replied', replies: [...(prev.replies || []), newReply] }));
      setReplyText('');
      setShowReply(false);
      setReplyFeedback('✓ Reply sent via Resend.');
    } catch (err) {
      setReplyFeedback(err.message);
    } finally {
      setSending(false);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');

  return (
    <div data-testid="admin-messages">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Contact Messages</h1>
          <p className="text-slate-400 text-sm">Submissions from the Contact Us form.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-slate-400 text-sm">{messages.length} total · {messages.filter((m) => m.status === 'new').length} new</span>
          <button onClick={fetchMessages} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition" data-testid="messages-refresh">
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="mb-4 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
        {/* List */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full">
              <thead className="bg-slate-800 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">From</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Subject</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">When</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {loading ? (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
                ) : messages.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-400">No messages yet.</td></tr>
                ) : messages.map((m) => (
                  <tr key={m.id} onClick={() => openMessage(m)}
                    className={`hover:bg-slate-800/50 cursor-pointer ${selected?.id === m.id ? 'bg-slate-800/70' : ''}`}
                    data-testid={`message-row-${m.id}`}>
                    <td className="px-4 py-3">
                      <div className={`text-sm ${m.status === 'new' ? 'text-white font-semibold' : 'text-slate-300'}`}>{m.name}</div>
                      <div className="text-xs text-slate-500">{m.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-sm">{m.subject || '(none)'}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmt(m.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${STATUS_STYLES[m.status] || STATUS_STYLES.read}`}>{m.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
          {selected ? (
            <div data-testid="message-detail">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-bold text-white">{selected.subject || '(no subject)'}</h2>
                  <p className="text-sm text-slate-400">{selected.name} · <a href={`mailto:${selected.email}`} className="text-emerald-400 hover:underline">{selected.email}</a></p>
                  <p className="text-xs text-slate-500 mt-1">{fmt(selected.created_at)}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setShowReply((s) => !s); setReplyFeedback(''); }}
                    className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition" data-testid="message-reply">
                    {showReply ? 'Close' : 'Reply'}
                  </button>
                  <button onClick={() => setStatus(selected, selected.status === 'archived' ? 'read' : 'archived')}
                    className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition" data-testid="message-archive">
                    {selected.status === 'archived' ? 'Unarchive' : 'Archive'}
                  </button>
                  <button onClick={() => del(selected)}
                    className="px-3 py-1.5 text-sm bg-red-600/30 hover:bg-red-600/50 text-red-300 rounded-lg transition" data-testid="message-delete">Delete</button>
                </div>
              </div>
              <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 text-slate-200 text-sm whitespace-pre-wrap">{selected.message}</div>

              {/* Sent replies history */}
              {(selected.replies || []).length > 0 && (
                <div className="mt-4 space-y-2" data-testid="message-reply-history">
                  {(selected.replies || []).map((r, i) => (
                    <div key={i} className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-3">
                      <p className="text-xs text-sky-300 mb-1">Replied by {r.sent_by} · {fmt(r.sent_at)}</p>
                      <p className="text-slate-200 text-sm whitespace-pre-wrap">{r.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply composer — sends via Resend */}
              {showReply && (
                <div className="mt-4 bg-slate-900/60 border border-slate-700 rounded-lg p-4" data-testid="reply-composer">
                  <label className="block text-xs text-slate-400 mb-2">
                    Reply to <span className="text-emerald-400">{selected.email}</span> — sent from your IntelliTix (Resend) address
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={6}
                    placeholder="Type your reply here..."
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 text-slate-100 text-sm rounded-lg focus:outline-none focus:border-emerald-500 resize-y"
                    data-testid="reply-textarea"
                  />
                  <div className="flex items-center gap-3 mt-3">
                    <button onClick={sendReply} disabled={sending || !replyText.trim()}
                      className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition" data-testid="reply-send">
                      {sending ? 'Sending…' : 'Send via Resend'}
                    </button>
                    <button onClick={() => { setShowReply(false); setReplyText(''); }}
                      className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition" data-testid="reply-cancel">Cancel</button>
                  </div>
                </div>
              )}
              {replyFeedback && (
                <p className={`mt-3 text-sm ${replyFeedback.startsWith('✓') ? 'text-emerald-400' : 'text-red-400'}`} data-testid="reply-feedback">{replyFeedback}</p>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm">Select a message to read it.</div>
          )}
        </div>
      </div>
    </div>
  );
}
